import { Alert, Divider, Link, Typography, useTheme } from '@mui/material';
import pino from 'pino';
import { useState } from 'react';
import type { ChangeEvent, FC, FocusEvent as ReactFocusEvent, FormEvent, PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { notificationStrings, sessionStrings } from '../../../../constants/strings';
import { userKeys } from '../../../../queries/query-keys';
import { useSessionLogoutQuery } from '../../../../queries/sessionQuery';
import { SessionRepository } from '../../../../repositories/sessionRepository';
import UserRepository from '../../../../repositories/userRepository';
import { queryClient } from '../../../../router/Root';
import { isCompanyOrSupportAdmin, isSupportAdmin } from '../../../../selectors/userSelectors';
import { useZustand } from '../../../../zustandStore';
import FormWrapper from '../hsbc/formWrapper';
import { useValidation } from '../hsbc/hooks/useValidation';
import FormInput from '../hsbc/steps/common/formInput';

const logger = pino();

const twoFactorSchema = z.object({
	authCode: z.string().min(6, 'Authentication code must be at least 6 characters long'),
});

type TwoFactorSchemaType = z.infer<typeof twoFactorSchema>;

type SetupTwoFactorAuthProps = {
	showNotificationMessage?: boolean;
};

export const invalidOtpCodeSentMessage = 'Incorrect code. Please try again.';

const SetupTwoFactorAuth: FC<PropsWithChildren<SetupTwoFactorAuthProps>> = ({ showNotificationMessage = false }) => {
	const navigate = useNavigate();
	const theme = useTheme();
	const logout = useSessionLogoutQuery();
	const [showSecretKey, setShowSecretKey] = useState(false);
	const { setPageLoading, registration, setNotification, setMfaData, setHasUserSkipMfa } = useZustand(state => ({
		...state,
	}));
	const { mfaData, showTwoFactorAuth } = registration;
	const [formState, setFormState] = useState<TwoFactorSchemaType>({
		authCode: '',
	});
	const { validateField, clearFieldError, fieldErrors, validateAllSchema } = useValidation<TwoFactorSchemaType>();
	const [isFormPending, setIsFormPending] = useState(false);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormState(prev => {
			const updatedState = { ...prev, [name]: value };
			clearFieldError(name as keyof TwoFactorSchemaType);
			return updatedState;
		});
	};

	const handleBlur = (e?: ReactFocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (e) {
			const { name = '' } = e.target;
			validateField(twoFactorSchema, name as keyof TwoFactorSchemaType, formState);
		}
	};

	const onActivateUser = async () => {
		try {
			const user = await UserRepository.getUser();
			queryClient.setQueryData([userKeys.getUser], user);

			const isTsAdmin = isSupportAdmin(user?.roles);

			if (user && !isTsAdmin && !isCompanyOrSupportAdmin(user?.roles) && user?.roles.length === 0) {
				logout.mutate();
				setNotification([
					{
						type: notificationStrings.ERROR,
						message: 'Your account has been disabled, please contact your company or team administrator.',
					},
				]);
			} else if (isTsAdmin) {
				void navigate('/settings/overview');
			} else {
				const path = isCompanyOrSupportAdmin(user?.roles || []) ? '/wallet' : '/wallet/my-cards';
				void navigate(path);
			}
		} catch (error) {
			logger.error({ message: 'error in onActivateUser', error });
			setNotification([
				{
					type: notificationStrings.ERROR,
					message: 'An error occurred while activating the user. Please try again.',
				},
			]);
		}
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsFormPending(true);
		validateAllSchema(twoFactorSchema, formState);

		if (!twoFactorSchema.safeParse(formState).success) {
			return;
		}
		try {
			const data = await SessionRepository.sendMfaCode({
				code: formState.authCode,
				challengeName: 'SOFTWARE_TOKEN_MFA',
			});
			setMfaData(data);
			if (data.mfaOtp.status === 'authenticated') {
				setFormState({ authCode: '' });
			}
			globalThis.localStorage.setItem('hasUserSkipMfa', 'false');
			return onActivateUser();
		} catch (error) {
			logger.error({ error });
			setNotification([
				{
					type: notificationStrings.ERROR,
					message: invalidOtpCodeSentMessage,
				},
			]);
		} finally {
			setIsFormPending(false);
			setPageLoading(false);
		}
	};

	const handleSkip = async () => {
		globalThis.localStorage.removeItem('hasUserSkipMfa');
		setHasUserSkipMfa(true);
		globalThis.localStorage.setItem('hasUserSkipMfa', 'true');

		try {
			await SessionRepository.sendMfaCodeChallengeSkip();
			return onActivateUser();
		} catch {
			setNotification([
				{
					type: notificationStrings.ERROR,
					message: 'An error occurred while skipping MFA. Please try again.',
				},
			]);
		}
	};

	return showTwoFactorAuth ? (
		<FormWrapper
			style={{ width: 'auto', border: 'none', paddingTop: 0 }}
			formName=""
			onSubmit={handleSubmit}
			submitButton={{
				label: sessionStrings.CONTINUE,
				isDisabled: !twoFactorSchema.safeParse(formState).success,
			}}
			isFormPending={isFormPending}
		>
			{globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP && showNotificationMessage ? (
				<Alert severity="warning" variant="filled" style={{ marginBottom: theme?.spacing(2) }}>
					Starting 30th of March, 2025, Go will require 2FA when accessing your account
				</Alert>
			) : null}

			<Typography variant="h5" gutterBottom>
				Enhance Your Security with 2FA
			</Typography>
			<Typography variant="body1" gutterBottom>
				Protect your account with Two-Factor Authentication (2FA) for an extra layer of security. Enabling 2FA
				significantly reduces unauthorized access, offering you peace of mind.
			</Typography>
			<Divider style={{ margin: '20px 0' }} />
			<Typography variant="h5" gutterBottom>
				Getting Started:
			</Typography>
			<Typography variant="body1" gutterBottom>
				<strong>1. Install an Authenticator App:</strong> (
				<Typography variant="caption">
					If you already have an app installed, you can proceed to <strong>Step 2</strong>.
				</Typography>
				) Choose and download a trusted app like Google Authenticator, Authy, or Microsoft Authenticator on your mobile
				device.
			</Typography>
			<Typography variant="body1" gutterBottom>
				<strong>2. Set Up Your Account:</strong> Open the app and follow the prompts to add an account. Scan the QR code
				provided to complete the setup.
			</Typography>
			<Typography variant="body1" gutterBottom>
				Ensure your account’s security with just a few easy steps!
			</Typography>

			<div style={{ textAlign: 'center', margin: '20px 0' }}>
				<img src={mfaData?.mfaOtp?.qrCode || ''} alt="QR Code" />
			</div>

			<Link
				href="#"
				onClick={e => {
					e.preventDefault();
					setShowSecretKey(!showSecretKey);
				}}
				style={{
					fontWeight: '600',
					fontSize: '12px',
					lineHeight: '20px',
					alignItems: 'center',
					textAlign: 'center',
					textTransform: 'uppercase',
					color: 'rgb(0, 174, 255)',
					display: 'block',
					margin: 'auto',
					width: '100%',
					textDecoration: 'none',
				}}
			>
				{showSecretKey ? 'HIDE SECRET KEY' : 'SHOW SECRET KEY'}
			</Link>
			{showSecretKey && (
				<Typography variant="body1" gutterBottom style={{ wordBreak: 'break-word' }}>
					Secret Key: {mfaData?.mfaOtp?.otpSecret || 'Not Available'}
				</Typography>
			)}

			<FormInput
				label={
					globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
						? sessionStrings.ENTER_YOUR_AUTH_APP_CODE_HSBC
						: sessionStrings.ENTER_YOUR_AUTH_APP_CODE_AMEX
				}
				name="authCode"
				value={formState.authCode}
				error={fieldErrors['authCode']}
				onChange={handleInputChange}
				onBlur={handleBlur}
			/>

			{mfaData?.skipMfaAllowed && !registration.hasUserSkipMfa ? (
				<>
					<Link
						href="#"
						onClick={e => {
							e.preventDefault();
							void handleSkip();
						}}
						style={{
							fontWeight: 'bold',
							fontSize: '14px',
							lineHeight: '24px',
							color: theme.palette.tsGoColor.red,
							display: 'block',
							padding: '10px',
							textAlign: 'center',
							textDecorationColor: theme.palette.tsGoColor.red,
						}}
					>
						Skip 2FA setup for now
					</Link>
					<Typography variant="subtitle2" gutterBottom style={{ wordBreak: 'break-word' }}>
						2FA will become mandatory to access Go starting on 30th of March, 2025
					</Typography>
				</>
			) : null}
		</FormWrapper>
	) : null;
};

export default SetupTwoFactorAuth;
